import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiEdit2, FiUpload, FiDownload, FiTrash2, FiPlay, FiCheckCircle,
  FiXCircle, FiClock, FiExternalLink, FiEye, FiLayers,
  FiRefreshCw, FiFilter, FiChevronDown, FiZap, FiBell, FiCornerUpLeft
} from 'react-icons/fi';
import { contractsAPI } from '../../services/api';
import { format, isToday, isYesterday } from 'date-fns';
import { formatDateLong, formatDateTimeFull, formatMonthDayTime, formatRelativeTime } from '../../utils/dateFormat';
import { localizeTimelineEvent } from '../../utils/timelineI18n';
import './ContractTimeline.scss';

const ICON_MAP = {
  create:        { Icon: FiPlus,          color: '#10b981', bg: '#ecfdf5' },
  edit:          { Icon: FiEdit2,         color: '#3b82f6', bg: '#eff6ff' },
  upload:        { Icon: FiUpload,        color: '#8b5cf6', bg: '#f5f3ff' },
  download:      { Icon: FiDownload,      color: '#64748b', bg: '#f1f5f9' },
  delete:        { Icon: FiTrash2,        color: '#ef4444', bg: '#fef2f2' },
  workflow:      { Icon: FiPlay,          color: '#6366f1', bg: '#eef2ff' },
  approve:       { Icon: FiCheckCircle,   color: '#10b981', bg: '#ecfdf5' },
  reject:        { Icon: FiXCircle,       color: '#ef4444', bg: '#fef2f2' },
  pending:       { Icon: FiClock,         color: '#f59e0b', bg: '#fffbeb' },
  external:      { Icon: FiExternalLink,  color: '#8b5cf6', bg: '#f5f3ff' },
  external_view: { Icon: FiEye,           color: '#14b8a6', bg: '#f0fdfa' },
  external_done: { Icon: FiCheckCircle,   color: '#059669', bg: '#ecfdf5' },
  version:       { Icon: FiLayers,        color: '#6366f1', bg: '#eef2ff' },
  // Information step: somebody was told, nobody was asked.
  notify:        { Icon: FiBell,          color: '#3b82f6', bg: '#eff6ff' },
  // Changes requested: a return, deliberately not the rejection red.
  changes:       { Icon: FiCornerUpLeft,  color: '#d97706', bg: '#fffbeb' },
  event:         { Icon: FiZap,           color: '#64748b', bg: '#f1f5f9' },
};

const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6'];
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

function matchesFilter(event, filter) {
  if (filter === 'all') return true;
  if (filter === 'workflow') return ['workflow_started', 'workflow_completed', 'workflow_rejected', 'approval_pending', 'step_approved', 'step_rejected', 'step_control_passed', 'step_control_failed', 'step_informed', 'step_skipped', 'step_changes_requested', 'step_returned_to_revision', 'edit_pending', 'audit'].includes(event.type) && ['workflow', 'approve', 'reject', 'pending', 'notify', 'changes', 'event'].includes(event.icon);
  if (filter === 'external') return event.type.startsWith('external');
  if (filter === 'edits') return ['version', 'created', 'version_created', 'version_activated', 'responsible_updated'].includes(event.type) || event.icon === 'edit';
  if (filter === 'documents') return ['upload', 'download'].includes(event.icon);
  return true;
}

function hexToRgba(hex, alpha) {
  const h = (hex || '#6366f1').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function avatarColor(str) {
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initialsOf(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
}

export default function ContractTimeline({ contractId }) {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const FILTER_GROUPS = useMemo(() => [
    { key: 'all',       label: t('contractTimeline.filters.all') },
    { key: 'workflow',  label: t('contractTimeline.filters.workflow') },
    { key: 'external',  label: t('contractTimeline.filters.external') },
    { key: 'edits',     label: t('contractTimeline.filters.edits') },
    { key: 'documents', label: t('contractTimeline.filters.documents') },
  ], [t]);

  const accent = useSelector(
    (s) => s.settings?.branding?.accentColor
      || s.settings?.config?.branding?.accentColor
      || '#6366f1'
  );

  useEffect(() => {
    if (!contractId) return;
    setLoading(true);
    contractsAPI.timeline(contractId)
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [contractId]);

  const filtered = useMemo(() => {
    const list = events.filter(e => matchesFilter(e, filter));
    // The step currently awaiting a decision is the contract's live state,
    // not history — keep it pinned above everything else regardless of
    // event timestamps.
    // 'edit_pending' is the same kind of live state: the contract is sitting
    // with its initiator waiting to be revised.
    const isLive = (e) => e.type === 'approval_pending' || e.type === 'edit_pending';
    const pending = list.filter(isLive);
    const rest = list.filter(e => !isLive(e));
    return [...pending, ...rest];
  }, [events, filter]);

  // Group events by date, with friendly Today / Yesterday headings.
  const grouped = useMemo(() => {
    const groups = {};
    for (const ev of filtered) {
      const d = new Date(ev.timestamp);
      const key = format(d, 'yyyy-MM-dd');
      const label = isToday(d) ? t('contractTimeline.today', { defaultValue: 'Today' })
        : isYesterday(d) ? t('contractTimeline.yesterday', { defaultValue: 'Yesterday' })
        : formatDateLong(d);
      if (!groups[key]) groups[key] = { label, events: [] };
      groups[key].events.push(ev);
    }
    return Object.values(groups);
  }, [filtered, t]);

  const accentVars = {
    '--tl-accent': accent,
    '--tl-accent-bg': hexToRgba(accent, 0.07),
    '--tl-accent-light': hexToRgba(accent, 0.15),
  };

  const actorName = (actor) => {
    if (!actor) return null;
    if (typeof actor === 'string') return OBJECT_ID_RE.test(actor) ? null : actor;
    return `${actor.firstName || ''} ${actor.lastName || ''}`.trim() || null;
  };

  // Combines the actor with whoever really performed the action. Two cases:
  // an impersonation session ("Y acted on behalf of X"), and a delegation —
  // where the record belongs to the delegator and the delegate is named as
  // having acted vekaleten ("X tarafından vekaleten — Y adına").
  const actorDisplay = (ev) => {
    const actor = actorName(ev.actor);
    const delegate = actorName(ev.delegate);
    if (actor && delegate) {
      return t('common.byDelegation', { delegate, delegator: actor });
    }
    const impersonator = actorName(ev.impersonator);
    if (actor && impersonator) {
      return t('common.onBehalfOf', { impersonator, impersonated: actor });
    }
    return actor;
  };

  // Human label for a single field-change entry (deduped to field names).
  const changeChipLabel = (c) => {
    if (c.group === 'table') return c.label || t('contractDetail.versionFields.status', { defaultValue: 'Table' });
    if (c.label) return c.label;
    if (c.group === 'fieldData') {
      return (c.field || '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (m) => m.toUpperCase());
    }
    return t(`contractDetail.versionFields.${c.field}`, { defaultValue: c.field });
  };

  const changeChips = (ev) => {
    if (!ev.changes?.length) return [];
    const seen = new Set();
    const labels = [];
    for (const c of ev.changes) {
      const label = changeChipLabel(c);
      if (label && !seen.has(label)) { seen.add(label); labels.push(label); }
    }
    return labels;
  };

  if (loading) {
    return (
      <div className="ctl" style={accentVars}>
        <div className="ctl__loading">
          <div className="ctl__spinner" />
          <span>{t('contractTimeline.loadingTimeline')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ctl" style={accentVars}>
      {/* ── Toolbar ── */}
      <div className="ctl__toolbar">
        <div className="ctl__toolbar-left">
          <h3 className="ctl__heading">
            <FiRefreshCw size={15} />
            {t('contractTimeline.title')}
          </h3>
          <span className="ctl__count">{t('contractTimeline.eventsCount', { count: filtered.length })}</span>
        </div>
        <button
          className={`ctl__filter-btn${filter !== 'all' ? ' ctl__filter-btn--active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FiFilter size={13} />
          {FILTER_GROUPS.find(f => f.key === filter)?.label || 'All'}
          <FiChevronDown size={13} className={showFilters ? 'ctl__chevron--open' : ''} />
        </button>
      </div>

      {/* ── Filter Chips ── */}
      {showFilters && (
        <div className="ctl__filters">
          {FILTER_GROUPS.map(f => (
            <button
              key={f.key}
              className={`ctl__chip${filter === f.key ? ' ctl__chip--active' : ''}`}
              onClick={() => { setFilter(f.key); setShowFilters(false); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Timeline ── */}
      {filtered.length === 0 ? (
        <div className="ctl__empty">
          <div className="ctl__empty-glyph"><FiClock size={26} /></div>
          <p>{t('contractTimeline.noActivity')}</p>
        </div>
      ) : (
        <div className="ctl__timeline">
          {grouped.map((group, gi) => (
            <div key={gi} className="ctl__group">
              <div className="ctl__date-row">
                <span className="ctl__date-pill">{group.label}</span>
              </div>
              <div className="ctl__events">
                {group.events.map((ev, ei) => {
                  const cfg = ICON_MAP[ev.icon] || ICON_MAP.event;
                  const EvIcon = cfg.Icon;
                  const isLast = ei === group.events.length - 1;
                  const isPending = ev.type === 'approval_pending';
                  const who = actorDisplay(ev);
                  const chips = changeChips(ev);
                  return (
                    <div key={`${gi}-${ei}`} className={`ctl__event${isPending ? ' ctl__event--pending' : ''}`}>
                      <div className="ctl__track">
                        <div className="ctl__dot" style={{ background: cfg.bg, color: cfg.color }}>
                          <EvIcon size={15} />
                        </div>
                        {!isLast && <div className="ctl__line" />}
                      </div>
                      <div className="ctl__card" style={{ '--ev-color': cfg.color }}>
                        <div className="ctl__card-top">
                          <span className="ctl__card-title">{localizeTimelineEvent(ev, 'title', t, i18n.language)}</span>
                          <span className="ctl__card-time" title={formatDateTimeFull(ev.timestamp)}>
                            {formatRelativeTime(ev.timestamp)}
                          </span>
                        </div>

                        {ev.description && (
                          <p className="ctl__card-desc">{localizeTimelineEvent(ev, 'description', t, i18n.language)}</p>
                        )}

                        {chips.length > 0 && (
                          <div className="ctl__changes">
                            <span className="ctl__changes-label">{t('contractTimeline.changedFields', { defaultValue: 'Changed' })}</span>
                            {chips.slice(0, 6).map((label, ci) => (
                              <span key={ci} className="ctl__change-chip">{label}</span>
                            ))}
                            {chips.length > 6 && <span className="ctl__change-chip ctl__change-chip--more">+{chips.length - 6}</span>}
                          </div>
                        )}

                        <div className="ctl__card-foot">
                          {who ? (
                            <span className="ctl__actor">
                              <span className="ctl__avatar" style={{ background: avatarColor(who) }}>{initialsOf(who)}</span>
                              {who}
                            </span>
                          ) : <span />}

                          <div className="ctl__badges">
                            {ev.metadata?.dueAt && (
                              <span className="ctl__badge ctl__badge--due">
                                <FiClock size={11} />
                                {t('contractTimeline.due', { date: formatMonthDayTime(ev.metadata.dueAt) })}
                              </span>
                            )}
                            {ev.metadata?.partyEmail && (
                              <span className="ctl__badge ctl__badge--email">{ev.metadata.partyEmail}</span>
                            )}
                            {isPending && (
                              <span className="ctl__badge ctl__badge--pending">{t('contractTimeline.awaitingDecision')}</span>
                            )}
                            {ev.type === 'step_approved' && (
                              <span className="ctl__badge ctl__badge--approved">{t('contractTimeline.approved')}</span>
                            )}
                            {ev.type === 'step_rejected' && (
                              <span className="ctl__badge ctl__badge--rejected">{t('contractTimeline.rejected')}</span>
                            )}
                            {ev.type === 'step_control_passed' && (
                              <span className="ctl__badge ctl__badge--approved">{t('contractTimeline.controlPassed', { defaultValue: 'Found suitable' })}</span>
                            )}
                            {ev.type === 'step_control_failed' && (
                              <span className="ctl__badge ctl__badge--rejected">{t('contractTimeline.controlFailed', { defaultValue: 'Not suitable' })}</span>
                            )}
                            {ev.type === 'step_informed' && (
                              <span className="ctl__badge ctl__badge--informed">{t('contractTimeline.informed')}</span>
                            )}
                            {ev.type === 'step_skipped' && (
                              <span className="ctl__badge ctl__badge--informed">{t('contractTimeline.skipped', { defaultValue: 'Skipped' })}</span>
                            )}
                            {ev.type === 'step_changes_requested' && (
                              <span className="ctl__badge ctl__badge--changes">{t('contractTimeline.changesRequested')}</span>
                            )}
                            {ev.type === 'step_returned_to_revision' && (
                              <span className="ctl__badge ctl__badge--changes">{t('contractTimeline.returnedToRevision')}</span>
                            )}
                            {/* The contract's live state after a send-back: which
                                decision put it there, and that it is still open. */}
                            {ev.type === 'edit_pending' && (
                              <span className={`ctl__badge ctl__badge--${ev.metadata?.approvalState === 'rejected' ? 'rejected' : 'changes'}`}>
                                {ev.metadata?.approvalState === 'rejected'
                                  ? t('contractTimeline.controlFailed', { defaultValue: 'Not suitable' })
                                  : t('contractTimeline.changesRequested')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
